<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Alertes & Valorisation Stock</h1>
        <p class="page-subtitle" v-if="products.length">{{ products.length }} produit{{ products.length > 1 ? 's' : '' }}</p>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn color="error" outlined small @click="exportPDF" v-if="products.length"><v-icon left small>mdi-file-pdf-box</v-icon>PDF</v-btn>
        <v-btn color="success" outlined small @click="exportExcel" v-if="products.length"><v-icon left small>mdi-file-excel</v-icon>Excel</v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Summary -->
      <v-row class="mb-2">
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#194173"><v-icon color="white" size="18">mdi-package-variant</v-icon></div>
            <div class="summary-val">{{ totalProducts }}</div>
            <div class="summary-lbl">Produits</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#4CAF50"><v-icon color="white" size="18">mdi-cash</v-icon></div>
            <div class="summary-val">{{ fmt(totalValuation) }}</div>
            <div class="summary-lbl">Valeur Totale (coût)</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#FF9800"><v-icon color="white" size="18">mdi-alert-outline</v-icon></div>
            <div class="summary-val warning--text">{{ lowStockCount }}</div>
            <div class="summary-lbl">Stock Bas</div>
          </v-card>
        </v-col>
        <v-col cols="6" md="3">
          <v-card flat class="summary-card">
            <div class="summary-icon-sm" style="background:#F44336"><v-icon color="white" size="18">mdi-minus-circle</v-icon></div>
            <div class="summary-val error--text">{{ negativeCount }}</div>
            <div class="summary-lbl">Stock Négatif</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filter tabs -->
      <v-card flat class="filter-card mb-4">
        <v-card-text class="py-2 px-3">
          <v-chip-group v-model="filter" mandatory active-class="primary--text">
            <v-chip small outlined>Tous ({{ products.length }})</v-chip>
            <v-chip small outlined color="error">Négatif ({{ negativeCount }})</v-chip>
            <v-chip small outlined color="warning">Stock Bas ({{ lowStockCount }})</v-chip>
            <v-chip small outlined color="success">Rupture = 0 ({{ zeroCount }})</v-chip>
          </v-chip-group>
        </v-card-text>
      </v-card>

      <!-- Table -->
      <v-card flat class="table-card" v-if="filteredProducts.length > 0">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">PRODUIT</th>
              <th class="text-left">CATÉGORIE</th>
              <th class="text-center">STOCK</th>
              <th class="text-center">SEUIL ALERTE</th>
              <th class="text-right">COÛT UNIT.</th>
              <th class="text-right">PRIX VENTE</th>
              <th class="text-right">VALEUR STOCK</th>
              <th class="text-center">STATUT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in filteredProducts" :key="p.id">
              <td class="text-left font-weight-medium">{{ p.name }}</td>
              <td class="text-left text-caption text--secondary">{{ p.category ? p.category.name : '—' }}</td>
              <td class="text-center font-weight-bold" :class="p.stock < 0 ? 'error--text' : ''">{{ p.stock }}</td>
              <td class="text-center text-caption">{{ p.stock_alert || '—' }}</td>
              <td class="text-right">{{ fmt(p.cost) }}</td>
              <td class="text-right">{{ fmt(p.price) }}</td>
              <td class="text-right font-weight-medium">{{ fmt(p.stock * p.cost) }}</td>
              <td class="text-center">
                <v-chip x-small :color="getStatusColor(p)" dark>{{ getStatusLabel(p) }}</v-chip>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
      <v-card flat v-else class="empty-card">
        <v-card-text class="text-center py-10">
          <v-icon size="48" color="success">mdi-check-circle-outline</v-icon>
          <p class="text-body-2 mt-3 text--secondary">Aucun produit dans cette catégorie</p>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'StockAlertView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    items: [{ text: 'Alertes Stock', disabled: true, href: '/stock-alerts' }],
    enable: false,
    products: [],
    filter: 0,
  }),

  computed: {
    totalProducts() { return this.products.length },
    totalValuation() { return this.products.reduce(function (s, p) { return s + (parseFloat(p.stock) || 0) * (parseFloat(p.cost) || 0) }, 0) },
    negativeCount() { return this.products.filter(function (p) { return parseFloat(p.stock) < 0 }).length },
    lowStockCount() { return this.products.filter(function (p) { return p.stock_alert > 0 && parseFloat(p.stock) <= p.stock_alert && parseFloat(p.stock) > 0 }).length },
    zeroCount() { return this.products.filter(function (p) { return parseFloat(p.stock) === 0 }).length },

    filteredProducts() {
      if (this.filter === 1) return this.products.filter(function (p) { return parseFloat(p.stock) < 0 })
      if (this.filter === 2) return this.products.filter(function (p) { return p.stock_alert > 0 && parseFloat(p.stock) <= p.stock_alert && parseFloat(p.stock) > 0 })
      if (this.filter === 3) return this.products.filter(function (p) { return parseFloat(p.stock) === 0 })
      return this.products
    },
  },

  methods: {
    getStatusColor(p) {
      if (parseFloat(p.stock) < 0) return 'error'
      if (parseFloat(p.stock) === 0) return 'grey'
      if (p.stock_alert > 0 && parseFloat(p.stock) <= p.stock_alert) return 'warning'
      return 'success'
    },
    getStatusLabel(p) {
      if (parseFloat(p.stock) < 0) return 'Négatif'
      if (parseFloat(p.stock) === 0) return 'Rupture'
      if (p.stock_alert > 0 && parseFloat(p.stock) <= p.stock_alert) return 'Bas'
      return 'OK'
    },

    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return (parseFloat(v) < 0 ? '-' : '') + p.join('.')
    },

    load() {
      var self = this
      this.$axios.get('/products?include=category&orderasc=name')
        .then(function (r) {
          self.products = (r.data || []).filter(function (p) { return p.status !== false })
          self.enable = true
        })
        .catch(function () { self.enable = true })
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], border = [220, 225, 230]

      doc.setFillColor(...primary); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('ALERTES & VALORISATION STOCK', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      if (biz.name) doc.text(biz.name, mr, 11, { align: 'right' })

      var y = 24
      var cols = [
        { label: 'PRODUIT', w: 48, align: 'left' },
        { label: 'CATÉGORIE', w: 28, align: 'left' },
        { label: 'STOCK', w: 16, align: 'center' },
        { label: 'SEUIL', w: 14, align: 'center' },
        { label: 'COÛT', w: 22, align: 'right' },
        { label: 'PRIX', w: 22, align: 'right' },
        { label: 'VALEUR', w: 24, align: 'right' },
        { label: 'STATUT', w: 18, align: 'center' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[0].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }
      drawHeader()

      self.filteredProducts.forEach(function (p, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var name = p.name || ''; if (name.length > 26) name = name.substring(0, 24) + '..'
        var cat = p.category ? p.category.name : '—'; if (cat.length > 14) cat = cat.substring(0, 12) + '..'
        var vals = [name, cat, String(p.stock), String(p.stock_alert || '—'), self.fmt(p.cost), self.fmt(p.price), self.fmt(p.stock * p.cost), self.getStatusLabel(p)]
        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          if (i === 2 && parseFloat(p.stock) < 0) doc.setTextColor(220, 38, 38); else doc.setTextColor(...dark)
          doc.text(vals[i], tx, y + 4.2, { align: c.align })
        })
        y += rowH
      })

      var totalPages = doc.internal.getNumberOfPages()
      for (var pg = 1; pg <= totalPages; pg++) {
        doc.setPage(pg); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + pg + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('alertes_stock.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var wsData = [['ALERTES & VALORISATION STOCK'], [biz.name || ''], [new Date().toLocaleDateString('fr-FR')], []]
      wsData.push(['Produit', 'Catégorie', 'Stock', 'Seuil Alerte', 'Coût', 'Prix', 'Valeur Stock', 'Statut'])
      self.filteredProducts.forEach(function (p) {
        wsData.push([p.name, p.category ? p.category.name : '', p.stock, p.stock_alert || 0, Math.round(p.cost * 100) / 100, Math.round(p.price * 100) / 100, Math.round(p.stock * p.cost * 100) / 100, self.getStatusLabel(p)])
      })
      wsData.push([]); wsData.push(['TOTAL', '', '', '', '', '', Math.round(self.totalValuation * 100) / 100, ''])
      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 8 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 10 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 4, c: 0 }, e: { r: 4, c: 7 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }]
      XLSX.utils.book_append_sheet(wb, ws, 'Alertes Stock'); XLSX.writeFile(wb, 'alertes_stock.xlsx')
    },
  },

  mounted() { this.load() },
}
</script>

<style scoped>
.summary-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; padding: 16px; text-align: center; }
.summary-icon-sm { width: 36px; height: 36px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.summary-val { font-size: 1.125rem; font-weight: 700; color: #1a2332; }
.summary-lbl { font-size: 0.75rem; color: #64748b; margin-top: 2px; }
.filter-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.table-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.table-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.table-card .v-simple-table tbody tr:hover { background: rgba(25, 65, 115, 0.03) !important; }
.empty-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
</style>
